import { GradingSystem, HomeworkTaskType } from '../types';

/** Trạng thái bài nộp (không dấu, đảm bảo độ chính xác cao) */
export const SUBMISSION_STATUS = {
  CHUA_NOP: 'chua-nop',
  DA_NOP: 'da-nop',
  DA_CHAM: 'da-cham',
} as const;

export type SubmissionStatus = (typeof SUBMISSION_STATUS)[keyof typeof SUBMISSION_STATUS];

/** Chuẩn hóa status từ API (có thể trả về dạng cũ có dấu) */
export function normalizeSubmissionStatus(status: string | undefined): SubmissionStatus {
  if (!status) return SUBMISSION_STATUS.CHUA_NOP;
  const s = String(status).toLowerCase().trim();
  if (s === 'da-cham' || s === 'đã chấm') return SUBMISSION_STATUS.DA_CHAM;
  if (s === 'da-nop' || s === 'đã nộp') return SUBMISSION_STATUS.DA_NOP;
  if (s === 'chua-nop' || s === 'chưa nộp') return SUBMISSION_STATUS.CHUA_NOP;
  return SUBMISSION_STATUS.CHUA_NOP;
}

/** Kiểm tra bài đã được giáo viên chấm */
export function isSubmissionGraded(status: string | undefined): boolean {
  return normalizeSubmissionStatus(status) === SUBMISSION_STATUS.DA_CHAM;
}

/** Kiểm tra bài đã nộp (không còn Chưa nộp) */
export function isSubmissionSubmitted(status: string | undefined): boolean {
  return normalizeSubmissionStatus(status) !== SUBMISSION_STATUS.CHUA_NOP;
}

/** Lấy submission cuối cùng (theo submittedDate) cho assignment + student */
export function getLastSubmission(
  submissions: { assignmentId: string; studentId: string; submittedDate?: string; [k: string]: any }[],
  assignmentId: string,
  studentId: string | null
): typeof submissions[0] | undefined {
  if (!studentId) return undefined;
  const matches = submissions.filter(
    s => String(s.assignmentId) === String(assignmentId) && String(s.studentId) === String(studentId)
  );
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  return [...matches].sort((a, b) => {
    const da = new Date(a.submittedDate || (a as any).submitted_date || 0).getTime();
    const db = new Date(b.submittedDate || (b as any).submitted_date || 0).getTime();
    return db - da;
  })[0];
}

/** Nhãn hiển thị theo mã trạng thái */
const STATUS_LABELS: Record<SubmissionStatus, string> = {
  [SUBMISSION_STATUS.CHUA_NOP]: 'Chưa nộp',
  [SUBMISSION_STATUS.DA_NOP]: 'Đã nộp',
  [SUBMISSION_STATUS.DA_CHAM]: 'Đã chấm',
};

/**
 * Hiển thị trạng thái phù hợp theo loại bài tập/nhiệm vụ.
 * - Bài tập (homework): Chưa nộp / Đã nộp / Đã chấm
 * - Nhiệm vụ (task): Chưa xác nhận / Đã xác nhận / Đã chấm
 * - Thông báo (announcement): Chưa xem / Đã xem
 */
export function getStatusDisplay(status: string, assignmentType?: string): string {
  const norm = normalizeSubmissionStatus(status);
  const label = STATUS_LABELS[norm];
  if (assignmentType === 'announcement') {
    if (norm === SUBMISSION_STATUS.CHUA_NOP) return 'Chưa xem';
    return 'Đã xem';
  }
  if (assignmentType === 'task') {
    if (norm === SUBMISSION_STATUS.CHUA_NOP) return 'Chưa xác nhận';
    if (norm === SUBMISSION_STATUS.DA_NOP) return 'Đã xác nhận';
  }
  return label;
}

/** Cấu hình khích lệ theo điểm (điểm 0-10 hoặc % 0-100). Dùng cho banner kết quả. */
export function getScoreFeedbackConfig(numericScore: number | undefined | null): { 
  label: string; 
  sublabel: string; 
  gradient: string; 
  shadow: string;
  iconBg: string;
} {
  const pct = numericScore == null ? 0 : numericScore <= 10 ? numericScore * 10 : numericScore;
  if (pct >= 85) return { label: 'Tuyệt vời!', sublabel: 'Con làm rất xuất sắc!', gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-600/20', iconBg: 'bg-white/20' };
  if (pct >= 70) return { label: 'Tốt lắm!', sublabel: 'Con đã hoàn thành tốt. Tiếp tục phát huy nhé!', gradient: 'from-teal-500 to-cyan-600', shadow: 'shadow-teal-600/20', iconBg: 'bg-white/20' };
  if (pct >= 50) return { label: 'Khá tốt!', sublabel: 'Con đã cố gắng. Ôn lại và làm tốt hơn lần sau nhé!', gradient: 'from-sky-500 to-blue-600', shadow: 'shadow-sky-600/20', iconBg: 'bg-white/20' };
  return { label: 'Cố gắng thêm nhé!', sublabel: 'Con hãy xem lại bài và ôn tập. Lần sau sẽ tốt hơn!', gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-600/20', iconBg: 'bg-white/20' };
}

/** Màu phân biệt theo loại: bài tập (xanh), nhiệm vụ (cam), thông báo (tím), đã chấm (xanh lá) */
export function getTypeColorConfig(type?: string, isGraded?: boolean): { bg: string; border: string; text: string; badge: string; label: string } {
  if (isGraded) return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-700', label: 'Đã chấm' };
  if (type === 'task') return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700', label: 'Nhiệm vụ' };
  if (type === 'announcement') return { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800', badge: 'bg-violet-100 text-violet-700', label: 'Thông báo' };
  return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-700', label: 'Bài tập' };
}

/** Parse submission.content (có thể là JSON string) để lấy answers */
export function parseSubmissionContent(submission: { content?: any } | null): Record<string, any> {
  if (!submission?.content) return {};
  const raw = submission.content;
  const parsed = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : (raw || {});
  return parsed.answers || {};
}

/** Chuẩn hóa answers từ API (key có thể khác task.id) sang Record<taskId, value> */
export function normalizeAnswersForTasks(
  tasks: { id: string; taskType?: string }[],
  rawAnswers: Record<string, any>
): Record<string, any> {
  if (!tasks?.length || !rawAnswers || typeof rawAnswers !== 'object') return {};
  const interactiveTasks = tasks.filter(t => t.taskType !== HomeworkTaskType.Instruction);
  const answerKeys = Object.keys(rawAnswers).sort();
  const result: Record<string, any> = {};
  interactiveTasks.forEach((task, idx) => {
    const direct = rawAnswers[task.id];
    if (direct !== undefined && direct !== null) {
      result[task.id] = direct;
      return;
    }
    const keyByTaskId = answerKeys.find(k => String(k).toLowerCase() === String(task.id).toLowerCase());
    if (keyByTaskId !== undefined) {
      result[task.id] = rawAnswers[keyByTaskId];
      return;
    }
    if (idx < answerKeys.length) result[task.id] = rawAnswers[answerKeys[idx]];
  });
  return result;
}

/** Quy đổi điểm từ hệ 10 sang các hệ khác (A, B, C, D, F hoặc Đạt/Chưa đạt) */
export function formatGrade(numericGrade: number | undefined | null, system: GradingSystem = 'numeric'): string {
    if (numericGrade === undefined || numericGrade === null) return 'Chưa chấm';
    
    if (system === 'letter') {
        if (numericGrade >= 8.5) return 'A';
        if (numericGrade >= 7.0) return 'B';
        if (numericGrade >= 5.5) return 'C';
        if (numericGrade >= 4.0) return 'D';
        return 'F';
    }

    if (system === 'pass_fail') {
        return numericGrade >= 5.0 ? 'Đạt' : 'Chưa đạt';
    }

    return numericGrade.toString();
}
